import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosConfig";
import { MDBRow, MDBCol } from "mdb-react-ui-kit";
import { Input } from "reactstrap";
import SwitchButton3 from "../../componentes/SwitchButton3";
import Swal from "sweetalert2";

const numberOr = (v, fb = 0) => (Number.isFinite(Number(v)) ? Number(v) : fb);

// Tamaño uniforme
const CONTROL_HEIGHT = 40; // px
const CONTROL_STYLE = {
    height: CONTROL_HEIGHT,
    minHeight: CONTROL_HEIGHT,
    width: "100%",
    boxSizing: "border-box",
};

const parseCsv = (s) =>
    String(s || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

export default function FormModalReportePowerBi({ initialData, onCancel }) {
    const init = useMemo(() => ({ ...(initialData || {}) }), [initialData]);
    const [form, setForm] = useState(init);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Categorías
    const [categorias, setCategorias] = useState([]);
    const [catLoading, setCatLoading] = useState(false);
    const [catError, setCatError] = useState(null);
    const [selectedCategoria, setSelectedCategoria] = useState(init.ReportCategoria ?? "");

    // ---- Roles (con switches) ----
    // Catálogo como objetos { id, name }
    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [rolesError, setRolesError] = useState(null);
    const [roleQuery, setRoleQuery] = useState("");

    // Preferimos IDs desde initialData si vienen; si no, nombres (legacy) y luego mapeamos a IDs.
    const initialRoleIds = parseCsv(init.RolesAutorizadosIds || init.RolesIds);
    const initialRoleNames = initialRoleIds.length ? [] : parseCsv(init.RolesAutorizados);

    const [selectedRoleIds, setSelectedRoleIds] = useState(initialRoleIds);

    const rolesById = useMemo(() => {
        const m = new Map();
        roles.forEach((r) => m.set(String(r.id), r));
        return m;
    }, [roles]);

    const norm = (s) => String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const filteredRoles = useMemo(() => {
        const q = norm(roleQuery);
        return roles.filter((r) => norm(r.name).includes(q));
    }, [roles, roleQuery]);

    const toggleRole = (id) => {
        setSelectedRoleIds((prev) =>
            prev.includes(String(id)) ? prev.filter((x) => x !== String(id)) : [...prev, String(id)]
        );
    };
    // --------------------------------

    const toBit = (v) => (v === true || v === 1 || v === "1" || v === "true" ? 1 : 0);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!String(form.NombreReporte || "").trim()) e.NombreReporte = "Requerido";
        if (!String(form.Id_PowerBI || "").trim()) e.Id_PowerBI = "Requerido";
        const cat = numberOr(selectedCategoria, null);
        if (cat === null || cat < 0) e.ReportCategoria = "Inválida";
        return e;
    };

    const handleSubmit = async (ev) => {
        ev?.preventDefault?.();
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length) return;

        const payload = {
            Id_Reporte: numberOr(form.Id_Reporte, -1),
            NombreReporte: String(form.NombreReporte || "").trim(),
            Id_PowerBI: String(form.Id_PowerBI || "").trim(),
            BitFiltroProyecto: toBit(form.BitFiltroProyecto), // 1/0
            BitFiltroEmpresa: toBit(form.BitFiltroEmpresa),   // 1/0
            ReportCategoria: numberOr(selectedCategoria, 0),
            // -> IDs separados por comas
            RolesAutorizados: selectedRoleIds.join(","),
        };

        try {
            setSubmitting(true);
            console.log("Submitting payload:", payload);
            await onSubmit(payload);
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = async (data) => {
        try {
            const res = await axios.post(
                process.env.REACT_APP_SERVER + "/operaciones/setreportesBI",
                JSON.stringify(data),
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("tkn")}`,
                    },
                    withCredentials: true,
                }
            );
            if (res.data?.success) {
                console.log("Response data powerbi:", res.data);
                // alert("Reporte guardado exitosamente.");
                Swal.fire({
                    icon: "success",
                    title: "Reporte guardado exitosamente",
                }).then(onCancel);
            } else {
                alert("Error guardando reporte: " + (res.data?.message || "Error desconocido"));
            }
        } catch (err) {
            alert("Error guardando reporte: " + (err?.message || "Error desconocido"));
        }
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        if (id === "ReportCategoria") setSelectedCategoria(value === "" ? "" : Number(value));
    };

    const getRoles = async () => {
        try {
            setRolesLoading(true);
            setRolesError(null);
            const res = await axios.get(
                process.env.REACT_APP_SERVER + "/operaciones/getroles",
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("tkn")}`,
                    },
                    withCredentials: true,
                }
            );

            const raw =
                (Array.isArray(res.data?.data) && res.data.data) ||
                (Array.isArray(res.data?.roles) && res.data.roles) ||
                (Array.isArray(res.data) && res.data) ||
                [];

            const mapId = (r) =>
                r.IdRol ?? r.IDRol ?? r.idRol ?? r.id_rol ?? r.RolID ?? r.RolId ?? r.ID ?? r.Id ?? r.value ?? r.id;
            const mapName = (r) =>
                r.NombreRol ?? r.nombreRol ?? r.Rol ?? r.nombre ?? r.name ?? r.descripcion ?? r.label;

            const list = raw
                .map((r) => (typeof r === "string" ? null : { id: String(mapId(r)), name: String(mapName(r) ?? mapId(r)) }))
                .filter((x) => x && x.id);

            setRoles(list);

            // Si venían nombres en initialData, mapearlos a IDs al cargar catálogo
            if (!initialRoleIds.length && initialRoleNames.length) {
                const idsFromNames = list
                    .filter((r) => initialRoleNames.includes(r.name))
                    .map((r) => String(r.id));
                if (idsFromNames.length) setSelectedRoleIds(idsFromNames);
            }
        } catch (error) {
            setRolesError(error?.message || "Error obteniendo roles");
        } finally {
            setRolesLoading(false);
        }
    };

    const getReportsCategories = async () => {
        try {
            setCatLoading(true);
            setCatError(null);
            const res = await axios.get(
                process.env.REACT_APP_SERVER + "/operaciones/getcategoriasBI",
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("tkn")}`,
                    },
                }
            );
            if (res.data?.success) setCategorias(Array.isArray(res.data.data) ? res.data.data : []);
            else setCatError(res.data?.message || "Error obteniendo categorías");
        } catch (err) {
            setCatError(err?.message || "Error de red");
        } finally {
            setCatLoading(false);
        }
    };

    useEffect(() => {
        getReportsCategories();
        getRoles();
    }, []);

    useEffect(() => {
        if (initialData && categorias.length > 0) {
            setSelectedCategoria(initialData.Id_ReportCategoria);
        }
    }, [initialData, categorias]);


    useEffect(() => {
        if (initialData && roles.length > 0) {
            console.log("Initial roles:", initialData);
            const initialRoleNames = parseCsv(initialData.RolesAutorizados || initialData.Roles || "");
            console.log("Initial role names:", initialRoleNames);
            console.log("Available roles:", roles);
            console.log("Available role IDs:", roles.map(r => r.id));
            const idsFromNames = roles
                .filter((r) => initialRoleNames.includes(r.id))
                .map((r) => String(r.id));
            console.log("Mapped role IDs from names:", idsFromNames);
            if (idsFromNames.length) setSelectedRoleIds(idsFromNames);
        }
    }, [initialData, roles]);

    // clases
    const labelCls = "Label d-block mb-1";
    const field = "mb-3";
    const error = (m) => <small className="text-danger d-block mt-1">{m}</small>;

    return (
        <form onSubmit={handleSubmit} className="p-3 w-100">
            <div className="form-card oc-compact oc-centered w-100" style={{ maxWidth: 720, margin: "0 auto" }}>
                <MDBRow className="gx-0 gy-2">
                    <MDBCol md="12">
                        {/* Categoría */}
                        <div className={field}>
                            <label className={labelCls} htmlFor="ReportCategoria">ReportCategoria</label>
                            <Input
                                id="ReportCategoria"
                                type="select"
                                style={CONTROL_STYLE}
                                value={selectedCategoria}
                                onChange={handleChange}
                                disabled={catLoading}
                                invalid={!!errors.ReportCategoria}
                            >
                                <option value="">{catLoading ? "Cargando categorías…" : "Seleccione..."}</option>
                                {categorias?.map((c) => {
                                    const id = c.Id_ReportCategoria ?? c.id ?? c.Id ?? c.ID ?? c.value;
                                    const label = c.ReportCategoria ?? c.nombre ?? c.name ?? c.Nombre ?? c.Descripcion ?? c.descripcion ?? String(id);
                                    return (
                                        <option key={String(id)} value={String(id)}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </Input>
                            {catError && <small className="text-danger d-block mt-1">{catError}</small>}
                            {errors.ReportCategoria && error(errors.ReportCategoria)}
                        </div>

                        {/* Nombre */}
                        <div className={field}>
                            <label className={labelCls} htmlFor="NombreReporte">NombreReporte</label>
                            <Input
                                id="NombreReporte"
                                type="text"
                                style={CONTROL_STYLE}
                                value={form.NombreReporte || ""}
                                onChange={(e) => set("NombreReporte", e.target.value)}
                                placeholder="Ej: Reporte de Ventas"
                                invalid={!!errors.NombreReporte}
                            />
                            {errors.NombreReporte && error(errors.NombreReporte)}
                        </div>

                        {/* ROLES: búsqueda + lista una columna, 200px con scroll */}
                        <div className={field}>
                            <label className={labelCls}>RolesAutorizados</label>

                            <Input
                                type="search"
                                placeholder="Buscar rol..."
                                value={roleQuery}
                                onChange={(e) => setRoleQuery(e.target.value)}
                                style={{ ...CONTROL_STYLE, marginBottom: 8 }}
                            />

                            <div className="border rounded p-2">
                                <div style={{ height: 200, overflowY: "auto" }}>
                                    {rolesLoading ? (
                                        <div className="text-muted" style={CONTROL_STYLE}>Cargando…</div>
                                    ) : filteredRoles.length ? (
                                        filteredRoles.map((r) => (
                                            <div
                                                key={r.id}
                                                className="d-flex align-items-center justify-content-between px-2 rounded mb-2"
                                                style={CONTROL_STYLE}
                                            >
                                                <span className="fw-medium">{r.name}</span>
                                                <SwitchButton3
                                                    checked={selectedRoleIds.includes(String(r.id))}
                                                    onChange={() => toggleRole(r.id)}
                                                    type={null}
                                                    disabled={submitting}
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-muted" style={CONTROL_STYLE}>Sin resultados</div>
                                    )}
                                </div>

                                {/* Chips seleccionados (muestra nombres a partir de IDs) */}
                                <div className="mt-2 d-flex flex-wrap gap-2">
                                    {selectedRoleIds.map((id) => (
                                        <span key={id} className="badge rounded-pill text-bg-primary">
                                            {rolesById.get(String(id))?.name ?? id}
                                        </span>
                                    ))}
                                    {!selectedRoleIds.length && <small className="text-muted">Sin roles seleccionados</small>}
                                </div>

                                {rolesError && <small className="text-danger d-block mt-2">{rolesError}</small>}
                            </div>
                        </div>

                        {/* Id PowerBI */}
                        <div className={field}>
                            <label className={labelCls} htmlFor="Id_PowerBI">Id_PowerBI</label>
                            <Input
                                id="Id_PowerBI"
                                type="textarea"
                                style={{ ...CONTROL_STYLE, resize: "none" }}
                                rows={1}
                                value={form.Id_PowerBI || ""}
                                onChange={(e) => set("Id_PowerBI", e.target.value)}
                                placeholder="abc123-powerbi-id"
                                invalid={!!errors.Id_PowerBI}
                            />
                            {errors.Id_PowerBI && error(errors.Id_PowerBI)}
                        </div>

                        {/* Filtros */}
                        <MDBRow className="g-3 mb-2">
                            <MDBCol md="6" sm="12">
                                <label className={labelCls}>Filtro Proyecto</label>
                                <div className="d-flex align-items-center px-2" style={CONTROL_STYLE}>
                                    <SwitchButton3
                                        checked={!!form.BitFiltroProyecto}
                                        onChange={(next) => set("BitFiltroProyecto", next)}
                                        type={null}
                                        disabled={submitting}
                                    />
                                </div>
                            </MDBCol>
                            <MDBCol md="6" sm="12">
                                <label className={labelCls}>Filtro Empresa</label>
                                <div className="d-flex align-items-center px-2" style={CONTROL_STYLE}>
                                    <SwitchButton3
                                        checked={!!form.BitFiltroEmpresa}
                                        onChange={(next) => set("BitFiltroEmpresa", next)}
                                        type={null}
                                        disabled={submitting}
                                    />
                                </div>
                            </MDBCol>
                        </MDBRow>

                        {/* Botones */}
                        <div className="d-flex justify-content-end gap-2 mt-2">
                            {onCancel && (
                                <button type="button" onClick={onCancel} className="px-4 py-2 btn btn-outline-secondary" disabled={submitting}>
                                    Cancelar
                                </button>
                            )}
                            <button type="submit" className="px-4 py-2 btn btn-primary" disabled={submitting}>
                                {submitting ? "Guardando…" : "Guardar"}
                            </button>
                        </div>
                    </MDBCol>
                </MDBRow>
            </div>
        </form>
    );
}
